import React, { useLayoutEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import classes from "../MasterDashboard.module.css";

const ContractVsSpotColumnChart = ({ title, subtitle, rows }) => {
  const maxTotal = Math.max(...rows.map((r) => r.contracted + r.spot), 1);
  const chartRef = useRef(null);
  const dotRefs = useRef([]);
  const [linePoints, setLinePoints] = useState([]);
  const [svgSize, setSvgSize] = useState({ width: 0, height: 0 });
  const [isOverflowing, setIsOverflowing] = useState(false);
  dotRefs.current = [];

  useLayoutEffect(() => {
    const chartEl = chartRef.current;
    if (!chartEl) return;
    const overflowing = chartEl.scrollWidth > chartEl.clientWidth + 1;
    setIsOverflowing((prev) => (prev !== overflowing ? overflowing : prev));
  }, [rows]);

  useLayoutEffect(() => {
    const measure = () => {
      const chartEl = chartRef.current;
      if (!chartEl) return;
      const points = dotRefs.current
        .filter(Boolean)
        .map((dotEl) => ({
          x: dotEl.offsetLeft + dotEl.offsetWidth / 2,
          y: dotEl.offsetTop + dotEl.offsetHeight / 2,
        }));
      setLinePoints(points);
      setSvgSize({ width: chartEl.scrollWidth, height: chartEl.scrollHeight });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [rows, isOverflowing]);

  return (
    <div className={classes.card}>
      <div className={classes.cardTitle}>{title}</div>
      <div className={classes.cardSubtitle}>{subtitle}</div>
      <div
        className={`${classes.columnChart} ${!isOverflowing ? classes.columnChartCentered : ""}`}
        ref={chartRef}
      >
        <svg
          className={classes.columnConnectorSvg}
          style={{ width: svgSize.width, height: svgSize.height }}
        >
          <polyline
            className={classes.columnConnectorLine}
            points={linePoints.map((p) => `${p.x},${p.y}`).join(" ")}
          />
        </svg>
        {rows.map((row, index) => {
          const total = row.contracted + row.spot;
          const heightRatio = Math.max(total / maxTotal, 0.08);
          return (
            <div className={classes.columnItem} key={row.label}>
              <div className={classes.columnSharePct}>{Number(row.spotSharePct).toFixed(1)}%</div>
              <div
                className={classes.columnDot}
                ref={(el) => {
                  dotRefs.current[index] = el;
                }}
              />
              <div className={classes.columnTotal}>{total}</div>
              <div
                className={classes.columnStack}
                style={{ "--col-height-ratio": heightRatio }}
              >
                <div
                  className={classes.columnSpot}
                  style={{ "--seg-height": `${(row.spot / total) * 100}%` }}
                />
                <div
                  className={classes.columnContracted}
                  style={{ "--seg-height": `${(row.contracted / total) * 100}%` }}
                />
              </div>
              <div className={classes.columnLabel} title={row.rangeLabel || undefined}>
                {row.label}
                {row.rangeLabel && <div className={classes.columnLabelRange}>{row.rangeLabel}</div>}
              </div>
            </div>
          );
        })}
      </div>
      <div className={classes.legend}>
        <span className={classes.legendItem}>
          <i className={classes.legendSwatchContracted} /> Contracted
        </span>
        <span className={classes.legendItem}>
          <i className={classes.legendSwatchSpot} /> Spot
        </span>
        <span className={classes.legendItem}>
          <i className={classes.legendSwatchShare} /> Spot share %
        </span>
      </div>
    </div>
  );
};

const INDENT_STATUS_SEGMENTS = [
  { key: "accepted", label: "Accepted", className: "segAccepted" },
  { key: "rejected", label: "Rejected", className: "segRejected" },
  { key: "pending", label: "Pending", className: "segPending" },
];

const DEFAULT_VISIBLE_N = 15;
const AXIS_TICKS = 4;

const displayTotal = (row) => row.accepted + row.rejected + row.pending;

const sortWithOthersLast = (rows) =>
  [...rows].sort((a, b) => {
    if (a.isOthers) return 1;
    if (b.isOthers) return -1;
    return displayTotal(b) - displayTotal(a);
  });

const buildTooltip = (row, total) =>
  [
    row.name,
    `Total: ${total}`,
    ...INDENT_STATUS_SEGMENTS.map(({ key, label }) => `${label}: ${row[key] || 0}`),
  ].join("\n");

const scaledHeightPct = (value, maxValue) => {
  if (maxValue <= 0) return 0;
  return (Math.sqrt(value) / Math.sqrt(maxValue)) * 100;
};

const BAR_ZONE_HEIGHT = 186;
const MAX_BAR_HEIGHT_PCT = 88;

const StatusColumnAxis = ({ maxTotal }) => {
  const ticks = Array.from({ length: AXIS_TICKS + 1 }, (_, i) => Math.round((maxTotal / AXIS_TICKS) * (AXIS_TICKS - i)));
  return (
    <div className={classes.statusColumnAxis}>
      <div className={classes.statusColumnAxisTrack} style={{ height: BAR_ZONE_HEIGHT }}>
        {ticks.map((tick, i) => (
          <span
            key={i}
            className={classes.statusColumnAxisTick}
            style={{
              bottom: `${scaledHeightPct(tick, maxTotal)}%`,
              transform: tick === 0 ? "translateY(0)" : "translateY(50%)",
            }}
          >
            {tick}
          </span>
        ))}
      </div>
    </div>
  );
};

const StatusColumns = ({ rows, centered }) => {
  const maxTotal = Math.max(...rows.map((r) => displayTotal(r)), 1);
  return (
    <div
      className={`${classes.statusColumnChart} ${centered ? classes.statusColumnChartCentered : ""}`}
    >
      <div className={classes.statusColumnGridlines} style={{ height: BAR_ZONE_HEIGHT }} />
      {rows.map((row) => {
        const total = displayTotal(row);
        const heightPct = Math.max(Math.min(scaledHeightPct(total, maxTotal), MAX_BAR_HEIGHT_PCT), 3);
        return (
          <div className={classes.statusColumnItem} key={row.name}>
            <div className={classes.statusColumnBarArea} style={{ height: BAR_ZONE_HEIGHT }}>
              <div
                className={classes.statusColumnTotal}
                style={{ bottom: `${heightPct}%` }}
              >
                {total}
              </div>
              <div
                className={classes.statusColumnBar}
                style={{ "--col-height": `${heightPct}%` }}
                title={buildTooltip(row, total)}
              >
                {INDENT_STATUS_SEGMENTS.map(({ key, className }) => {
                  const count = row[key];
                  if (!count) return null;
                  const segPct = (count / total) * 100;
                  return (
                    <div
                      key={key}
                      className={classes[className]}
                      style={{ height: `${segPct}%`, width: "100%" }}
                    />
                  );
                })}
              </div>
            </div>
            <div
              className={classes.statusColumnLabel}
              title={[row.address, row.code].filter(Boolean).join(" | ") || row.name}
            >
              {row.code ? (
                <>
                  <strong>{row.code}</strong> - {row.name.slice(row.code.length + 3)}
                </>
              ) : (
                row.name
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const IndentStatusLegend = () => (
  <div className={`${classes.legend} ${classes.statusColumnLegend}`}>
    <span className={classes.legendItem}>
      <i className={classes.legendSwatchAccepted} /> Accepted
    </span>
    <span className={classes.legendItem}>
      <i className={classes.legendSwatchRejected} /> Rejected
    </span>
    <span className={classes.legendItem}>
      <i className={classes.legendSwatchPending} /> Pending
    </span>
  </div>
);

const downloadIndentStatus = (rows, fileName, isPlantView = false) => {
  if (!rows.length) {
    toast.error("Data is not available to download");
    return;
  }
  const exportRows = rows.map((row, index) =>
    isPlantView
      ? {
        "Sr No": index + 1,
        "Location Code": row.code || "—",
        "Location Name": row.locationName || row.name,
        Total: displayTotal(row),
        Accepted: row.accepted || 0,
        Rejected: row.rejected || 0,
        Pending: row.pending || 0,
      }
      : {
        Name: row.name,
        Total: displayTotal(row),
        Accepted: row.accepted || 0,
        Rejected: row.rejected || 0,
        Pending: row.pending || 0,
      }
  );

  const ws = XLSX.utils.json_to_sheet(exportRows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Indent Status");
  XLSX.writeFile(wb, fileName);
};

const IndentStatusTable = ({ rows, isPlantView = false }) => (
  <div className={classes.tableWrap}>
    <table className={classes.table}>
      <thead>
        <tr>
          {isPlantView ? (
            <>
              <th style={{ textAlign: "center", width: "6%" }}>Sr No</th>
              <th style={{ textAlign: "center", width: "16%" }}>Location Code</th>
              <th style={{ textAlign: "center", width: "26%" }}>Location Name</th>
            </>
          ) : (
            <th style={{ textAlign: "center", width: "30%" }}>Name</th>
          )}
          <th style={{ textAlign: "center" }}>Total</th>
          <th style={{ textAlign: "center" }}>Accepted</th>
          <th style={{ textAlign: "center" }}>Rejected</th>
          <th style={{ textAlign: "center" }}>Pending</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={row.name}>
            {isPlantView ? (
              <>
                <td style={{ textAlign: "center" }}>{index + 1}</td>
                <td style={{ textAlign: "center", fontWeight: 700 }}>{row.code || "—"}</td>
                <td
                  className={classes.plantCell}
                  title={row.address || row.locationName}
                  style={{ whiteSpace: "normal", textAlign: "center" }}
                >
                  {row.locationName || row.name}
                </td>
              </>
            ) : (
              <td
                className={classes.plantCell}
                title={[row.address, row.code].filter(Boolean).join(" | ") || row.name}
                style={{ whiteSpace: "normal", textAlign: "center" }}
              >
                {row.fullName || row.name}
              </td>
            )}
            <td style={{ textAlign: "center", fontWeight: 700 }}>{displayTotal(row)}</td>
            <td style={{ textAlign: "center" }}>{row.accepted || 0}</td>
            <td style={{ textAlign: "center" }}>{row.rejected || 0}</td>
            <td style={{ textAlign: "center" }}>{row.pending || 0}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const IndentStatusBarList = ({
  title,
  subtitle,
  rows,
  allRows,
  isPlantView = false,
  viewOnly = false,
  onRestrictedClick = () => {},
}) => {
  const [expanded, setExpanded] = useState(false);
  const sortedRows = sortWithOthersLast(rows);
  const othersRow = sortedRows.find((r) => r.isOthers) || null;
  const rankedRows = othersRow ? sortedRows.filter((r) => !r.isOthers) : sortedRows;
  const sortedAllRows = sortWithOthersLast(allRows || rows);
  const allOthersRow = sortedAllRows.find((r) => r.isOthers) || null;
  const rankedAllRows = allOthersRow ? sortedAllRows.filter((r) => !r.isOthers) : sortedAllRows;
  const hasMore = rankedAllRows.length > DEFAULT_VISIBLE_N - (othersRow ? 1 : 0);
  const topRankedRows = rankedRows.slice(0, DEFAULT_VISIBLE_N - (othersRow ? 1 : 0));
  const topRows = othersRow ? [...topRankedRows, othersRow] : topRankedRows;
  const topMaxTotal = Math.max(...topRows.map((r) => displayTotal(r)), 1);
  const exportFileName = `${title.toLowerCase().replace(/[^a-z0-9]+/g, "_")}.xlsx`;
  const restrictedButtonStyle = viewOnly ? { opacity: 0.6, cursor: "not-allowed" } : undefined;

  return (
    <div className={classes.card}>
      <div className={classes.cardHeaderRow}>
        <div>
          <div className={classes.cardTitle}>{title}</div>
          <div className={classes.cardSubtitle}>{subtitle}</div>
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <button
            type="button"
            className={classes.fullScreenButton}
            style={restrictedButtonStyle}
            disabled={viewOnly}
            onClick={() => (viewOnly ? onRestrictedClick() : downloadIndentStatus(sortedAllRows, exportFileName, isPlantView))}
            title="Download as Excel"
          >
            ⬇ Download
          </button>
          {hasMore && (
            <button
              type="button"
              className={`${classes.fullScreenButton} ${classes.fullScreenButtonLarge}`}
              style={restrictedButtonStyle}
              disabled={viewOnly}
              onClick={() => (viewOnly ? onRestrictedClick() : setExpanded(true))}
              title="View all"
            >
              ⛶ View all  ({rankedAllRows.length})
            </button>
          )}
        </div>
      </div>

      <div className={classes.statusColumnChartWrap}>
        <StatusColumnAxis maxTotal={topMaxTotal} />
        <div className={classes.statusColumnScroll}>
          <StatusColumns rows={topRows} centered={!hasMore} />
        </div>
      </div>
      <IndentStatusLegend />

      {expanded && (
        <div className={classes.modalOverlay} onClick={() => setExpanded(false)}>
          <div className={`${classes.modalPanel} ${classes.modalPanelWide}`} onClick={(e) => e.stopPropagation()}>
            <div className={classes.modalHeaderRow}>
              <div>
                <div className={classes.cardTitle}>{title}</div>
                <div className={classes.cardSubtitle}>{subtitle}</div>
              </div>
              <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                <button
                  type="button"
                  className={classes.fullScreenButton}
                  style={restrictedButtonStyle}
                  disabled={viewOnly}
                  onClick={() => (viewOnly ? onRestrictedClick() : downloadIndentStatus(sortedAllRows, exportFileName, isPlantView))}
                  title="Download as Excel"
                >
                  ⬇ Download
                </button>
                <button
                  type="button"
                  className={classes.modalCloseButton}
                  onClick={() => setExpanded(false)}
                >
                  ✕
                </button>
              </div>
            </div>
            <div className={classes.modalScrollArea} style={{ flex: 1, minHeight: 0 }}>
              <IndentStatusTable rows={rankedAllRows} isPlantView={isPlantView} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Pie slice path helper ─── */
const polarPoint = (cx, cy, r, angleDeg) => {
  const angleRad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(angleRad), y: cy + r * Math.sin(angleRad) };
};

const pieSlicePath = (cx, cy, r, startAngle, endAngle) => {
  if (endAngle - startAngle >= 359.999) {
    // full circle — draw as two arcs
    const mid = startAngle + 180;
    const p1 = polarPoint(cx, cy, r, startAngle);
    const pMid = polarPoint(cx, cy, r, mid);
    const p2 = polarPoint(cx, cy, r, endAngle);
    return `M ${cx} ${cy} L ${p1.x} ${p1.y} A ${r} ${r} 0 1 1 ${pMid.x} ${pMid.y} A ${r} ${r} 0 1 1 ${p2.x} ${p2.y} Z`;
  }
  const start = polarPoint(cx, cy, r, startAngle);
  const end = polarPoint(cx, cy, r, endAngle);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y} Z`;
};

const IndentSummaryCard = ({ kpis }) => {
  const raised = kpis.raised || 0;
  const spot = kpis.movedToSpot || 0;
  const contracted = Math.max(raised - spot, 0);
  const spotPct = raised ? Math.round((spot / raised) * 100) : 0;
  const contractedPct = 100 - spotPct;

  const [hoveredSlice, setHoveredSlice] = useState(null);

  // pie geometry — two slices (contracted, spot)
  const size = 64;
  const radius = size / 2;
  const cx = size / 2;
  const cy = size / 2;
  const contractedAngle = raised ? (contracted / raised) * 360 : 0;

  const pieSlices = [
    {
      key: "contracted",
      label: "Contracted",
      value: contracted,
      pct: contractedPct,
      color: "#2c2f7c",
      startAngle: 0,
      endAngle: contractedAngle,
    },
    {
      key: "spot",
      label: "Spot",
      value: spot,
      pct: spotPct,
      color: "#8b89d6",
      startAngle: contractedAngle,
      endAngle: 360,
    },
  ].filter((s) => s.value > 0);

  const ringStats = [
    { label: "Accepted", value: kpis.accepted, sublabel: `${kpis.acceptedPct.toFixed(1)}%`, color: "#2f9e58" },
    { label: "Rejected", value: kpis.rejected, sublabel: `${kpis.rejectedPct.toFixed(1)}%`, color: "#c74343" },
    { label: "Pending", value: kpis.pending, sublabel: `${kpis.pendingPct.toFixed(1)}%`, color: "#f0a942" },
    { label: "Expired", value: kpis.expired, sublabel: `${kpis.expiredPct.toFixed(1)}%`, color: "#c74343" },
    { label: "Cancelled", value: kpis.cancelled, sublabel: `${kpis.cancelledPct.toFixed(1)}%`, color: "#9ca0ad" },
  ];

  return (
    <div className={`${classes.card} ${classes.indentSummaryCard}`}>
      <div className={classes.donutRing}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {pieSlices.map((s) => (
            <path
              key={s.key}
              className={classes.pieSlice}
              d={pieSlicePath(cx, cy, radius, s.startAngle, s.endAngle)}
              fill={s.color}
              onMouseEnter={() => setHoveredSlice(s.key)}
              onMouseLeave={() => setHoveredSlice(null)}
            >
              <title>{`${s.label}: ${s.value} (${s.pct}%)`}</title>
            </path>
          ))}
        </svg>
        {hoveredSlice && (
          <div className={classes.pieTooltip}>
            {pieSlices.find((s) => s.key === hoveredSlice)?.label}:{" "}
            {pieSlices.find((s) => s.key === hoveredSlice)?.pct}%
          </div>
        )}
      </div>
      <div className={classes.donutRingBody}>
        <div className={classes.statLabel}>Indents raised</div>
        <div className={classes.statValue}>{raised}</div>
        <div className={classes.donutRingLegend}>
          <span>
            <i style={{ background: "#2c2f7c" }} /> Contracted {contracted}
            <span className={classes.statBadge}>{contractedPct}%</span>
          </span>
          <span>
            <i style={{ background: "#8b89d6" }} /> Spot {spot}
            <span className={classes.statBadge}>{spotPct}%</span>
          </span>
        </div>
      </div>
      <div className={classes.ringStatList}>
        {ringStats.map((s) => (
          <div
            key={s.label}
            className={classes.ringStatItem}
            style={{ borderTopColor: s.color }}
          >
            <div className={classes.ringStatLabel}>{s.label}</div>
            <div className={classes.ringStatValue} style={{ color: s.color }}>
              {s.value}
            </div>
            <span
              className={classes.statBadge}
              style={{ background: `${s.color}1a`, color: s.color }}
            >
              {s.sublabel}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const IndentSummaryTab = ({
  indentSummary,
  filteredIndentsByPlant,
  allIndentsByPlant,
  filteredIndentsByTransporter,
  viewOnly = false,
  onRestrictedClick = () => {},
}) => {
  if (!indentSummary.kpis.raised) {
    return (
      <div className={classes.placeholderCard}>
        <div className={classes.placeholderTitle}>No data available for this period</div>
        <div className={classes.placeholderText}>
          Please change the period or filters to see indent activity.
        </div>
      </div>
    );
  }

  return (
    <>
      <IndentSummaryCard kpis={indentSummary.kpis} />

      {indentSummary.raisedByPeriod.length > 0 && (
        <ContractVsSpotColumnChart
          title="Indents Raised — Contracted vs Spot"
          rows={indentSummary.raisedByPeriod}
        />
      )}

      <IndentStatusBarList
        title="Indent Status — By Transporter"
        rows={filteredIndentsByTransporter}
        viewOnly={viewOnly}
        onRestrictedClick={onRestrictedClick}
      />

      <IndentStatusBarList
        title=" Indent Status — By Plant"
        rows={filteredIndentsByPlant}
        allRows={allIndentsByPlant}
        isPlantView
        viewOnly={viewOnly}
        onRestrictedClick={onRestrictedClick}
      />
    </>
  );
};

export default IndentSummaryTab;
